<template>
  <div class="recycle-list">
    <template v-if="tableList.length">
      <a-table
        class="project-table"
        :columns="columns"
        rowKey="id"
        :dataSource="tableList"
        :loading="loading"
        :scroll="{ y: tableHeight }"
        :pagination="false"
        :bordered="false"
      >
        <template slot="projectName" slot-scope="text, record">
          <a-tooltip :title="record.projectName">
            {{ record.projectName }}
          </a-tooltip>
        </template>
        <template slot="coverPath" slot-scope="text, record">
          <img
            :src="getImageURL(record)"
            alt=""
            style="
              width: 93px;
              height: 56px;
              object-fit: cover;
              aspect-ratio: 4/3;
              border-radius: 4px 4px 4px 4px;
              border: 1px solid #8a8a8a;
            "
          />
        </template>
        <template slot="remainDays" slot-scope="text, record">
          <span>{{ record.remainDays }}天</span>
        </template>
        <template slot="operations" slot-scope="text, record">
          <AuthControl code="recover_recycle_web">
            <a-button type="link" @click="handleOperations(record, 'recovery')">{{ $t('project.recover') }}</a-button>
          </AuthControl>
          <AuthControl code="permanently_delete_recycle_web">
            <a-button type="link" @click="handleOperations(record, 'completelyDelete')">{{
              $t('project.thoroughDelete')
            }}</a-button>
          </AuthControl>
        </template>
      </a-table>
    </template>
    <!-- 分页组件固定在页面右下角 -->
    <div class="pagination-container" v-if="tableList.length">
      <a-pagination
        show-size-changer
        :current="pagination.current"
        :total="pagination.total"
        :show-total="(total) => $t('project.total', { total })"
        show-quick-jumper
        :page-size="pagination.pageSize"
        @change="handleTableChange"
        @showSizeChange="handleParamsChange"
      />
    </div>
    <a-empty
      v-if="loaded && tableList.length === 0"
      class="emptycss"
      :image="emptyDataImg"
      :description="$t('project.noSearchResults')"
    />
    <TipsModal ref="tipsModalRef" />
  </div>
</template>

<script>
import TipsModal from './tipsModal.vue'
import { getProjRecycleList, deleteRecycleProject, resetProject } from '@/api/recycleBin'
import emptyDataImg from '@/assets/images/empty-data.png'
export default {
  name: 'RecycleList',
  components: {
    TipsModal,
  },
  props: {
    tabKey: {
      type: String,
      required: true,
    },
    searchKey: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      emptyDataImg,
      loaded: false,
      loading: false,
      tableList: [],
      tableHeight: window.innerHeight - 360,
      currentRecord: null,
      pagination: {
        current: 1,
        pageSize: 20,
        showTotal: true,
        showSizeChanger: true,
        pageSizeOptions: ['20', '50', '100'],
      },
      defaultPicture: require('@/assets/images/default-card.png'),
    }
  },
  computed: {
    userId() {
      const userInfo = JSON.parse(localStorage.getItem('userInfo')) || {}
      return userInfo.applyAccount || userInfo.userId || null
    },
    columns() {
      return [
        {
          title: this.$t('project.serialNumber'),
          customRender: (text, record, index) => index + 1,
          width: 140,
        },
        {
          title: this.$t('project.projectName'),
          dataIndex: 'projectName',
          scopedSlots: { customRender: 'projectName' },
          align: 'left',
          ellipsis: true,
          show: true,
        },
        {
          title: this.$t('project.previewImg'),
          dataIndex: 'coverPath',
          scopedSlots: { customRender: 'coverPath' },
          align: 'left',
          show: true,
        },
        {
          title: this.$t('project.remainingTime'),
          dataIndex: 'remainDays',
          scopedSlots: { customRender: 'remainDays' },
          align: 'left',
          ellipsis: true,
          show: true,
        },
        {
          title: this.$t('project.uploadTime'),
          dataIndex: 'updateTime',
          align: 'left',
          ellipsis: true,
          show: true,
        },
        {
          title: this.$t('project.operations'),
          scopedSlots: { customRender: 'operations' },
          align: 'left',
          width: 280,
        },
      ]
    },
  },
  watch: {
    tabKey: {
      handler (val){
        this.getTableList()
      },
      immediate: false,
    },
    searchKey: {
      handler (val){
        this.getTableList()
      },
      immediate: false,
    },
    tableList(newVal) {
      this.$emit('refreshTableList', newVal)
    },
  },
  mounted() {
    this.initTable()
    this.getTableList()
    window.addEventListener('resize', this.initTable)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.initTable)
  },
  methods: {
    initTable() {
      this.tableHeight = window.innerHeight - 360
    },

    handleTableChange(page, pageSize) {
      this.pagination.current = page
      this.pagination.pageSize = pageSize
      this.getTableList()
    },

    handleParamsChange(current, size) {
      this.pagination.current = current || 1
      this.pagination.pageSize = size || this.pagination.pageSize
      this.getTableList()
    },

    async handleOperations(record, type) {
      switch (type) {
        case 'recovery':
          // 恢复
          try {
            const res = await resetProject({ projectId: record.id })
            if (res.code === 200) {
              this.$message.success('恢复成功')
              this.getTableList()
            }
          } catch (error) {
            console.error('恢复失败:', error)
          }
          break
        case 'completelyDelete':
          // 彻底删除
          this.currentRecord = record
          this.$refs.tipsModalRef.showModal({
            title: this.$t('project.confirmDelete'),
            content: this.$t('project.confirmDeleteTip'),
            icon: 'warn',
            confirmCallback: () => {
              this.handleCompletelyDelete()
            },
            cancelCallback: () => {},
          })
          break
      }
    },

    async handleCompletelyDelete() {
      if (!this.currentRecord) return
      try {
        // 在这里调用删除 API
        const payload = {
          account: this.userId,
          isClearAll: 1,
          projectIds: [this.currentRecord.id],
        }
        await deleteRecycleProject(payload)
        this.$message.success('删除成功')
        // 刷新列表
        this.getTableList()
      } catch (error) {
        console.error('删除失败:', error)
      }
    },

    getImageURL(_record) {
      if (!_record.coverPath) return this.defaultPicture
      const imgHost = _record.coverPath.indexOf('http') > -1 ? '' : process.env.VUE_APP_CTYUN_URL
      return _record.coverPath ? `${imgHost}${_record.coverPath}` : this.defaultPicture
    },

    async getTableList() {
      this.loading = true
      try {
        let res
        if (this.tabKey === 'myRecycle') {
          // 我的项目回收站
          const params = {
            account: this.userId,
            queryParam: {
              searchKey: this.searchKey || '',
            },
            pageInfo: {
              pageSize: this.pagination.pageSize,
              pageIndex: this.pagination.current,
            },
            sortInfo: {},
          }
          res = await getProjRecycleList(params)
          if (res?.data) {
            this.tableList = res.data.records
            this.pagination.total = res.data.total
          }
        }
      } catch (error) {
        console.error('获取回收站数据失败:', error)
      } finally {
        this.loaded = true
        this.loading = false
      }
    },
  },
}
</script>
<style lang="less" scoped>
/* 为表格容器添加底部边距，防止分页组件遮挡 */
.recycle-list {
  padding-bottom: 80px;
  /deep/ .project-table {
    .ant-table-body {
      // 自定义滚动条样式
      &::-webkit-scrollbar {
        display: block;
        width: 8px;
      }

      &::-webkit-scrollbar-track {
        background: rgba(255, 255, 255, 0.05);
        border-radius: 4px;
      }

      &::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.2);
        border-radius: 4px;
        transition: background 0.3s ease;
      }

      &::-webkit-scrollbar-thumb:hover {
        background: rgba(255, 255, 255, 0.4);
      }
    }
  }
}

/* 分页组件固定在页面右下角 */
.pagination-container {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  background-color: transparent;
}
.emptycss {
  margin: 0 auto;
  margin-top: 200px;
  :deep(.ant-empty-image img) {
    width: 143.48px;
    height: 146px;
  }
}
</style>